import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { HelpTooltip } from "../help/HelpTooltip";

type ReviewLogRow = {
  id: number;
  created_at: string;
  offer_slug: string | null;
  channel: string | null;
  step_key: string | null;
  problems: string[];
  action: "aprobado" | "reescrito" | "base_segura";
  intentos: number;
};

const ACTION_LABEL: Record<ReviewLogRow["action"], string> = {
  aprobado: "Aprobado",
  reescrito: "Reescrito por la IA",
  base_segura: "Cayó a la base segura",
};

/** T.2: rechazos del revisor automático (reglas locales) al generar mensajes.
 * "Aprobado" nunca llega aquí: msgMigrateWork solo registra cuando falló al menos
 * un intento (ver ai/index.ts, msgMigrateWork). */
export const MsgReviewSection = () => {
  const { data: rows } = useQuery({
    queryKey: ["msg_review_log"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("msg_review_log")
        .select(
          "id, created_at, offer_slug, channel, step_key, problems, action, intentos",
        )
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as ReviewLogRow[];
    },
  });

  return (
    <Card className="gap-3 p-4">
      <h2 className="inline-flex items-center gap-1 text-base font-extrabold">
        Rechazos del revisor de mensajes
        <HelpTooltip topic="templates.review" />
      </h2>
      <p className="text-xs text-muted-foreground">
        Cada vez que un mensaje generado no pasó las reglas a la primera (usted,
        opción de baja, largo, número sin condicional, prueba social y precio
        contra la ficha del producto, frases prohibidas): qué falló y cómo se
        resolvió.
      </p>
      {!rows?.length ? (
        <p className="text-sm text-muted-foreground">Ningún rechazo todavía.</p>
      ) : (
        <div className="flex flex-col divide-y">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-col gap-1 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">
                  {r.offer_slug ?? "—"} · {r.step_key ?? r.channel}
                </span>
                <span
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-xs font-semibold",
                    r.action === "base_segura" &&
                      "border-[var(--critical)] text-[var(--critical)]",
                  )}
                >
                  {ACTION_LABEL[r.action]} · {r.intentos} intento
                  {r.intentos === 1 ? "" : "s"}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString("es-MX")}
                </span>
              </div>
              <ul className="list-disc pl-5 text-xs text-muted-foreground">
                {r.problems.map((p, idx) => (
                  <li key={idx}>{p}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
